"use client";

import {useEffect, useRef, useState} from "react";
import {clipboardHasPlainText, getImageFilesFromClipboard} from "@/utils/chat/clipboardImageFiles";
import {getIncompatibleFiles} from "@/utils/fileHelpers";
import {CONTEXT_WHEEL_VISIBILITY_THRESHOLD} from "../ContextWheel";
import type {ChatInputDictationApi, ChatInputProps} from "./types";

export type ChatInputComposerParams = ChatInputProps & {
   dictation?: ChatInputDictationApi | null;
   inputRef: React.RefObject<HTMLTextAreaElement | null>;
};

export function useChatInputComposer({
   value,
   onChange,
   onSubmit,
   disabled = false,
   isSubmitting = false,
   autoFocus = true,
   selectedAssistant,
   showUploadButton = true,
   onUploadClick,
   uploadDisabled = false,
   onFilesSelected,
   fileUploadConfig,
   attachedFiles = [],
   onFilesChange,
   fileCompatibilityTooltip = "",
   tokenUsagePercent = 0,
   tokenLimitReached = false,
   dictation = null,
   inputRef,
}: ChatInputComposerParams) {
   const containerRef = useRef<HTMLDivElement>(null);
   const fileInputRef = useRef<HTMLInputElement>(null);
   const scrollContainerRef = useRef<HTMLDivElement>(null);
   const [isUploadButtonAnimating, setIsUploadButtonAnimating] = useState(false);
   const [queuedMessage, setQueuedMessage] = useState<string | null>(null);
   const prevIsSubmitting = useRef(isSubmitting);
   const [isFocused, setIsFocused] = useState(false);
   const [scrollState, setScrollState] = useState({
      canScrollLeft: false,
      canScrollRight: false,
   });

   const shouldShowUploadButton = showUploadButton && fileUploadConfig?.enabled;
   const prevShouldShowUploadButton = useRef(shouldShowUploadButton);
   const showModelSelector = !!selectedAssistant?.model;
   const incompatibleFiles = getIncompatibleFiles(attachedFiles, fileUploadConfig);
   const hasIncompatibleFiles = incompatibleFiles.length > 0;
   const hasToolsContent = shouldShowUploadButton || showModelSelector;
   const hasContent = !!value.trim() || attachedFiles.length > 0;
   const canSubmit = !disabled && hasContent && !hasIncompatibleFiles;
   const showDictationButton = !!dictation;
   const sendTooltip = hasIncompatibleFiles ? fileCompatibilityTooltip : undefined;
   // Always show 100% when the limit has been reached - the gauge label says "Limit reached"
   // so the bar should be full regardless of the last reported percent.
   const displayedTokenUsagePercent = tokenLimitReached ? 100 : tokenUsagePercent;
   const shouldShowContextWheel =
      (displayedTokenUsagePercent >= CONTEXT_WHEEL_VISIBILITY_THRESHOLD || tokenLimitReached) &&
      displayedTokenUsagePercent > 0;

   const updateScrollState = () => {
      if (!scrollContainerRef.current) return;
      const container = scrollContainerRef.current;
      setScrollState({
         canScrollLeft: container.scrollLeft > 0,
         canScrollRight: container.scrollLeft < container.scrollWidth - container.clientWidth,
      });
   };

   useEffect(() => {
      if (prevShouldShowUploadButton.current && !shouldShowUploadButton) {
         setIsUploadButtonAnimating(true);
      }
      prevShouldShowUploadButton.current = shouldShowUploadButton;
   }, [shouldShowUploadButton]);

   useEffect(() => {
      const timer = setTimeout(updateScrollState, 100);
      return () => clearTimeout(timer);
   }, [attachedFiles]);

   useEffect(() => {
      if (!autoFocus) return;
      const timer = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(timer);
   }, [autoFocus, inputRef]);

   useEffect(() => {
      if (prevIsSubmitting.current && !isSubmitting && queuedMessage !== null) {
         setQueuedMessage(null);
         onSubmit(queuedMessage || undefined);
      }
      prevIsSubmitting.current = isSubmitting;
   }, [isSubmitting, queuedMessage, onSubmit]);

   const handleFiles = async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      await onFilesSelected?.(Array.from(files));
   };

   const canAcceptPastedImages =
      !disabled &&
      !isSubmitting &&
      !uploadDisabled &&
      !!shouldShowUploadButton &&
      !!onFilesSelected;

   const handlePaste = (e: React.ClipboardEvent<HTMLFormElement>) => {
      if (!canAcceptPastedImages) return;

      const images = getImageFilesFromClipboard(e.clipboardData);
      if (images.length === 0) return;

      // Image-only paste would otherwise insert nothing useful in a textarea.
      // Mixed clipboard keeps the default so any copied text still lands.
      if (!clipboardHasPlainText(e.clipboardData)) {
         e.preventDefault();
      }

      void onFilesSelected(images);
   };

   const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      await handleFiles(e.target.files);
      if (fileInputRef.current) fileInputRef.current.value = "";
      inputRef.current?.focus();
   };

   const handleUploadClick = () => {
      if (onUploadClick) {
         onUploadClick();
      } else {
         fileInputRef.current?.click();
      }
   };

   const handleRemoveFile = (index: number) => {
      if (index < 0 || index >= attachedFiles.length) return;
      onFilesChange?.(attachedFiles.filter((_, i) => i !== index));
   };

   const submitOrQueue = () => {
      if (!canSubmit) return;

      dictation?.cancel();

      if (isSubmitting) {
         setQueuedMessage(value.trim());
         onChange("");
         if (inputRef.current) inputRef.current.style.height = "auto";
         return;
      }

      onSubmit();
   };

   const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.nativeEvent.isComposing || e.keyCode === 229) return;
      if (e.key === "Enter" && !e.shiftKey) {
         e.preventDefault();
         submitOrQueue();
      }
   };

   const handleFormSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      submitOrQueue();
   };

   // Make the whole composer box behave like the textarea: clicking any
   // non-interactive area (padding, empty space, control row) focuses the input.
   // Attached imperatively so the container stays a non-interactive element in
   // JSX (jsx-a11y/no-static-element-interactions); the textarea remains the
   // real keyboard-accessible control.
   useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const handleMouseDown = (e: MouseEvent) => {
         const target = e.target as HTMLElement;
         if (target.closest("button, a, input, textarea, [role='combobox']")) {
            return;
         }
         e.preventDefault();
         inputRef.current?.focus();
      };

      container.addEventListener("mousedown", handleMouseDown);
      return () => container.removeEventListener("mousedown", handleMouseDown);
   }, [inputRef]);

   return {
      containerRef,
      fileInputRef,
      scrollContainerRef,
      isUploadButtonAnimating,
      setIsUploadButtonAnimating,
      queuedMessage,
      setQueuedMessage,
      isFocused,
      setIsFocused,
      scrollState,
      shouldShowUploadButton,
      showModelSelector,
      incompatibleFiles,
      hasToolsContent,
      canSubmit,
      showDictationButton,
      sendTooltip,
      displayedTokenUsagePercent,
      shouldShowContextWheel,
      updateScrollState,
      handlePaste,
      handleFileInputChange,
      handleUploadClick,
      handleRemoveFile,
      handleKeyDown,
      handleFormSubmit,
   };
}
